import { useEffect, useRef, useState } from "react";
import { FiPaperclip } from "react-icons/fi";
const WS_URL = process.env.REACT_APP_WS_BASE_URL;
export default function ChatModal({ isOpen, onClose, groupName, groupId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [preview, setPreview] = useState(null);
  const socketRef = useRef(null);

  // ✅ ADDED: scroll ref
  const messagesEndRef = useRef(null);

  // ---------------- BASE64 ----------------
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  // ---------------- AUTO SCROLL ----------------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ---------------- CONNECT SOCKET ----------------
  useEffect(() => {
    if (!isOpen || !groupId) return;

    const token = localStorage.getItem("access_token");

    const socket = new WebSocket(
      `${WS_URL}/ws/marketplace/group_chat/${groupId}/?token=${token}`
    );

    socketRef.current = socket;

    socket.onopen = () => {
      console.log("WebSocket connected");
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === "history") {
        setMessages(
          data.messages.map((msg) => ({
            id: msg.id,
            text: msg.message,
            file: msg.file,
            sender: msg.message_by,
            senderInfo: {
              id: msg.sender?.id,
              name: msg.sender_profile?.name || msg.sender?.username || "Unknown User",
              profile_picture: msg.sender_profile?.profile_picture,
              email: msg.sender_profile?.email,
            },
            time: msg.timestamp,
          }))
        );
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          id: data.id || Date.now(),
          text: data.message,
          file: data.file,
          sender: data.message_by,
          senderInfo: {
            id: data.sender?.id,
            name: data.sender_profile?.name || data.sender?.username || "Unknown User",
            profile_picture: data.sender_profile?.profile_picture,
            email: data.sender_profile?.email,
          },
          time: data.timestamp,
        },
      ]);
    };

    socket.onclose = () => {
      console.log("WebSocket disconnected");
    };

    return () => socket.close();
  }, [isOpen, groupId]);

  // ---------------- FILE SELECT + AUTO SEND ----------------
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setPreview({
      name: file.name,
      url: previewUrl,
      type: file.type,
    });

    const base64 = await toBase64(file);

    socketRef.current?.send(
      JSON.stringify({
        type: "file",
        file_data: base64,
        file_name: file.name,
      })
    );

    setPreview(null);
  };

  // ---------------- SEND TEXT ----------------
  const handleSend = () => {
    if (!input.trim()) return;

    socketRef.current?.send(
      JSON.stringify({
        type: "text",
        message: input,
      })
    );

    setInput("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col h-[500px]">

        {/* HEADER */}
        <div className="flex justify-between items-center p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                {groupName}
              </h2>
              <p className="text-xs text-gray-500">Group ID: {groupId}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* CHAT AREA */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">

          {messages.map((msg) => (
            <div key={msg.id} className="space-y-1">
              <div className={`flex items-center gap-2 mb-1 ${
                msg.sender === "me" ? "justify-end" : "justify-start"
              }`}>
                {msg.sender !== "me" && (
                  <>
                    {msg.senderInfo?.profile_picture ? (
                      <img 
                        src={msg.senderInfo.profile_picture} 
                        alt={msg.senderInfo.name}
                        className="w-7 h-7 rounded-full object-cover border-2 border-white shadow-sm"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#e8f0f7] flex items-center justify-center text-xs font-medium text-[#0f6abf]">
                        {msg.senderInfo?.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <span className="text-xs font-medium text-gray-700">
                      {msg.senderInfo?.name}
                    </span>
                  </>
                )}
                {msg.sender === "me" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">
                      You
                    </span>
                    {msg.senderInfo?.profile_picture && (
                      <img 
                        src={msg.senderInfo.profile_picture} 
                        alt="You"
                        className="w-7 h-7 rounded-full object-cover border-2 border-white shadow-sm"
                      />
                    )}
                  </div>
                )}
              </div>

              <div
                className={`max-w-[75%] px-4 py-2.5 rounded-xl text-sm ${
                  msg.sender === "me"
                    ? "bg-[#0f6abf] text-white ml-auto rounded-br-sm"
                    : "bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm"
                }`}
              >
                {msg.text && <div className="break-words">{msg.text}</div>}

                {msg.file && (
                  <a
                    href={msg.file}
                    target="_blank"
                    rel="noreferrer"
                    className="block mt-2 text-xs underline flex items-center gap-1"
                  >
                    Download File
                  </a>
                )}

                {msg.time && (
                  <div className={`text-[10px] mt-1.5 ${msg.sender === "me" ? "text-white/70" : "text-gray-400"}`}>
                    {msg.time}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* ✅ ADDED: scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* INPUT AREA */}
        <div className="p-3 border-t border-gray-100 flex items-center gap-2 bg-white">
          <label className="cursor-pointer p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <input
              type="file"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>

          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="Type a message..."
            className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm"
          />

          <button
            onClick={handleSend}
            className="bg-[#0f6abf] text-white px-4 py-2.5 rounded-xl text-sm"
          >
            Send
          </button>
        </div>

      </div>
    </div>
  );
}